import { useEffect, useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { ACCOUNT_TYPE_LABEL } from '../../lib/accountTypeLabel';
import { listAccounts } from '../accounts/api';
import type { Account } from '../../types/account';
import type { AudienceScope } from '../../types/broadcast';
import type { ResolverAccount } from '../../lib/broadcastAudience';
import {
  computeNodeStates,
  toggleNodeSelection,
  type NodeSelectionState,
} from '../../lib/broadcastAudienceTree';

// Stage 6A v2 §2.3 — audience tree-select. Renders the ACTIVE accounts under the
// chosen Brand(s) as an indented tri-state checkbox forest. Brands ARE
// selectable nodes now (cross-brand, no inheritance — a brand contributes its
// own direct contacts only when ticked). Clicking a node with children cascades
// over its subtree; clicking a leaf toggles it individually.
//
// Selection state is derived from the shared resolution logic so the checkbox
// states always match what the server will resolve.

type AudienceTreeSelectProps = {
  // The Brand account ids to render as roots (Specific mode passes the chosen
  // brands; the All-Brands path hides the tree entirely).
  rootBrandIds: string[];
  scope: AudienceScope;
  onChange: (next: AudienceScope) => void;
};

type TreeNode = {
  account: Account;
  depth: number;
  hasChildren: boolean;
};

function buildChildrenByParent(accounts: Account[]): Map<string, Account[]> {
  const map = new Map<string, Account[]>();
  for (const a of accounts) {
    if (!a.parentAccountId) continue;
    const bucket = map.get(a.parentAccountId) ?? [];
    bucket.push(a);
    map.set(a.parentAccountId, bucket);
  }
  for (const bucket of map.values()) bucket.sort((a, b) => a.name.localeCompare(b.name));
  return map;
}

// Depth-first flatten of each root Brand + its active descendants. The Brand is
// rendered at depth 0 (selectable). Active accounts only.
function flattenForest(rootBrandIds: string[], accounts: Account[]): TreeNode[] {
  const active = accounts.filter((a) => (a.status ?? 'active') === 'active');
  const byId = new Map(active.map((a) => [a.id, a]));
  const childrenByParent = buildChildrenByParent(active);
  const out: TreeNode[] = [];
  const walk = (parentId: string, depth: number) => {
    for (const child of childrenByParent.get(parentId) ?? []) {
      const grandchildren = childrenByParent.get(child.id) ?? [];
      out.push({ account: child, depth, hasChildren: grandchildren.length > 0 });
      walk(child.id, depth + 1);
    }
  };
  for (const brandId of [...rootBrandIds].sort((a, b) =>
    (byId.get(a)?.name ?? '').localeCompare(byId.get(b)?.name ?? ''),
  )) {
    const brand = byId.get(brandId);
    if (!brand) continue;
    out.push({ account: brand, depth: 0, hasChildren: (childrenByParent.get(brand.id) ?? []).length > 0 });
    walk(brand.id, 1);
  }
  return out;
}

const STATE_DESC: Record<NodeSelectionState, string> = {
  checked: 'Selected',
  partial: 'Partially selected',
  unchecked: 'Not selected',
};

export function AudienceTreeSelect({ rootBrandIds, scope, onChange }: AudienceTreeSelectProps) {
  const accountsQuery = useQuery({ queryKey: ['accounts'], queryFn: listAccounts });
  const accounts = accountsQuery.data;

  const [search, setSearch] = useState('');
  const [debounced, setDebounced] = useState('');
  useEffect(() => {
    const t = window.setTimeout(() => setDebounced(search.trim().toLowerCase()), 200);
    return () => window.clearTimeout(t);
  }, [search]);

  const nodes = useMemo(
    () => (accounts ? flattenForest(rootBrandIds, accounts) : []),
    [accounts, rootBrandIds],
  );

  // The active forest as ResolverAccounts for the selection-state logic.
  const subtreeAccounts = useMemo<ResolverAccount[]>(
    () =>
      nodes.map((n) => ({
        id: n.account.id,
        parentAccountId: n.account.parentAccountId,
        accountType: n.account.accountType,
        status: 'active',
      })),
    [nodes],
  );

  const states = useMemo(
    () => computeNodeStates(subtreeAccounts, scope),
    [subtreeAccounts, scope],
  );

  const visible = useMemo(() => {
    if (!debounced) return nodes;
    return nodes.filter((n) => n.account.name.toLowerCase().includes(debounced));
  }, [nodes, debounced]);

  if (accountsQuery.isLoading) {
    return <p className="text-sm text-slate-500">Loading accounts…</p>;
  }
  if (accountsQuery.error) {
    return (
      <p className="text-sm text-red-600">
        Failed to load account hierarchy: {(accountsQuery.error as Error).message}
      </p>
    );
  }
  if (nodes.length === 0) {
    return <p className="text-sm text-slate-500">No active accounts for the selected brand(s).</p>;
  }

  const toggle = (node: TreeNode) => {
    onChange(toggleNodeSelection(node.account.id, subtreeAccounts, scope));
  };

  return (
    <div className="space-y-2">
      <input
        type="search"
        className="w-full rounded border border-slate-300 px-2 py-1 text-sm focus:outline-none focus:ring-2 focus:ring-slate-400"
        placeholder="Filter by name…"
        value={search}
        onChange={(e) => setSearch(e.target.value)}
      />
      <ul className="max-h-96 overflow-y-auto pr-1 text-sm">
        {visible.length === 0 ? (
          <li className="px-2 py-1 italic text-slate-400">No accounts match.</li>
        ) : (
          visible.map((node) => {
            const state = states.get(node.account.id) ?? 'unchecked';
            return (
              <li key={node.account.id} className="py-0.5">
                <label
                  className="flex cursor-pointer items-center gap-2 rounded px-1 hover:bg-slate-50"
                  style={{ paddingLeft: `${node.depth * 1.25 + 0.25}rem` }}
                  title={STATE_DESC[state]}
                >
                  <input
                    type="checkbox"
                    checked={state === 'checked'}
                    ref={(el) => {
                      if (el) el.indeterminate = state === 'partial';
                    }}
                    onChange={() => toggle(node)}
                  />
                  <span
                    className={
                      node.account.accountType === 'brand'
                        ? 'font-semibold text-slate-900'
                        : node.hasChildren
                          ? 'font-medium text-slate-900'
                          : 'text-slate-900'
                    }
                  >
                    {node.account.name}
                  </span>
                  <span className="text-xs text-slate-400">
                    ({ACCOUNT_TYPE_LABEL[node.account.accountType]})
                  </span>
                </label>
              </li>
            );
          })
        )}
      </ul>
    </div>
  );
}
